import { useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useFilters, periodLabel, comparisonLabel, periodToDates } from "../lib/filters";
import {
  IconFilter, IconDocument, IconWhatsApp, IconSync,
  IconUser, IconLogout, IconChevronDown, IconRefresh,
} from "./icons";
import { USER_ROLE_LABELS } from "@bi/types";
import { useState } from "react";
import { SIDEBAR_WIDTH, useSidebarCollapsed } from "../lib/sidebar";

const PAGE_TITLES: Record<string, string> = {
  "/governanca": "Governança",
  "/frota/manutencao": "Central de Manutenção",
  "/":                "Visão Executiva",
  "/financeiro":      "Financeiro",
  "/operacional":     "Operacional CT-e",
  "/custos":          "Custos",
  "/dre":             "DRE / Resultado",
  "/fluxo-caixa":     "Fluxo de Caixa",
  "/insights":        "Insights",
  "/alertas":         "Alertas",
  "/relatorios":      "Relatórios",
  "/whatsapp":        "WhatsApp",
  "/qualidade-dados": "Qualidade dos Dados",
  "/sincronizacao":   "Sincronização",
  "/configuracoes":   "Configurações",
};

interface Props {
  onFilterToggle: () => void;
  filterOpen: boolean;
}

export function Topbar({ onFilterToggle, filterOpen }: Props) {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { filters } = useFilters();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarCollapsed] = useSidebarCollapsed();

  const title = PAGE_TITLES[pathname] ?? (pathname.startsWith("/frota/manutencao/") ? "Manutenção" : "Inteligência SIAG");
  const restricted = user?.role === "manutencao";
  // Telas de Sistema sem período: filtro, relatório e envio no WhatsApp não se aplicam.
  const governance = ["/governanca", "/qualidade-dados", "/sincronizacao", "/configuracoes"].includes(pathname);
  // Telas de Manutenção com filtro de período PRÓPRIO (na página) ignoram o
  // filtro global; mostrar "Este mês" e "vs. período anterior" no topo dava a
  // entender que trocar ali mudava a tela. Custos e Checklists usam o global.
  const ownPeriodFilter = pathname.startsWith("/frota/manutencao")
    && !["/frota/manutencao/custos", "/frota/manutencao/checklists"].includes(pathname);
  // WhatsApp tem período próprio (30/90 dias/tudo) na página.
  const hideGlobalFilter = governance || ownPeriodFilter || pathname === "/whatsapp";

  // Compute the date interval for display in the filter button
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit",
      timeZone: "America/Sao_Paulo",
    });
  const periodRange = `${fmtDate(from)} – ${fmtDate(new Date(new Date(to).getTime() - 1).toISOString())}`;

  return (
    <header className={`fixed top-0 ${sidebarCollapsed ? SIDEBAR_WIDTH.collapsed.topbar : SIDEBAR_WIDTH.open.topbar} right-0 h-16 bg-white border-b border-surface-border z-30 flex items-center px-6 gap-4 transition-[left] duration-200`}>
      {/* Page title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
        {!restricted && !hideGlobalFilter && filters.comparison !== "none" && (
          <p className="text-xs text-gray-400">
            vs. {comparisonLabel(filters.comparison)}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Filter toggle — shows current period label */}
        {!hideGlobalFilter && <button
          onClick={onFilterToggle}
          className={`btn-secondary gap-2 ${filterOpen ? "bg-tm-50 border-tm-200 text-tm-700" : ""}`}
          title={`Filtro ativo: ${periodLabel(filters.period)} (${periodRange})`}
        >
          <IconFilter className="w-4 h-4" />
          <span className="hidden lg:inline text-xs font-medium">{periodLabel(filters.period)}</span>
          <span className="hidden sm:inline lg:hidden text-xs">Filtros</span>
        </button>

        }
        {/* Generate report */}
        {!restricted && !governance && <button className="btn-secondary hidden md:inline-flex">
          <IconDocument className="w-4 h-4" />
          <span>Relatório</span>
        </button>

        }
        {/* WhatsApp summary */}
        {!restricted && !governance && <button className="btn-primary hidden md:inline-flex">
          <IconWhatsApp className="w-4 h-4" />
          <span>Enviar no WhatsApp</span>
        </button>

        }
        {/* Sync status */}
        {!restricted && !governance && <div className="hidden lg:flex items-center gap-1.5 text-xs text-gray-400 bg-surface-muted px-3 py-2 rounded-lg border border-surface-border">
          <IconSync className="w-3.5 h-3.5 text-tm-500" />
          <span>Sync 14h32</span>
        </div>}

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((o) => !o)}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-surface-muted transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-tm-100 text-tm-700 grid place-items-center">
              <IconUser className="w-4 h-4" />
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700 max-w-[120px] truncate">{user?.name ?? "Usuário"}</span>
            {user && <span className="rounded-full bg-tm-50 px-2 py-0.5 text-xs font-semibold text-tm-700">{USER_ROLE_LABELS[user.role]}</span>}
            <IconChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-card-lg border border-surface-border py-1 z-50">
              <div className="px-4 py-2 border-b border-surface-border">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                <p className="text-xs text-gray-400 capitalize mt-0.5">{user ? USER_ROLE_LABELS[user.role] : ""}</p>
              </div>
              {!restricted && <button
                onClick={() => { setUserMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-surface-muted text-left"
              >
                <IconRefresh className="w-4 h-4" />
                Sincronizar agora
              </button>}
              <button
                onClick={() => { setUserMenuOpen(false); signOut(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 text-left"
              >
                <IconLogout className="w-4 h-4" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
